import React, { useMemo, useState } from "react";
import "./CalendarView.css";

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toLocalYMD = (value) => {
  if (!value) return null;
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CalendarView = ({ pendingTasks = [], missedTasks = [], completedTasks = [], onNavigateToTasks }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  const monthMeta = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const start = new Date(year, month, 1);
    const startDay = start.getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    return { year, month, startDay, totalDays };
  }, [viewDate]);

  const tasksByDate = useMemo(() => {
    const map = {};
    const add = (task, bucket) => {
      const key = toLocalYMD(task.due_date);
      if (!key) return;
      if (!map[key]) map[key] = { pending: [], missed: [], completed: [] };
      map[key][bucket].push(task);
    };

    pendingTasks.forEach((t) => add(t, "pending"));
    missedTasks.forEach((t) => add(t, "missed"));
    completedTasks.forEach((t) => add(t, "completed"));

    return map;
  }, [pendingTasks, missedTasks, completedTasks]);

  const allTasksWithStatus = useMemo(() => {
    const withStatus = [];
    pendingTasks.forEach(t => withStatus.push({ ...t, status: "Pending" }));
    missedTasks.forEach(t => withStatus.push({ ...t, status: "Overdue" }));
    completedTasks.forEach(t => withStatus.push({ ...t, status: "Completed" }));
    return withStatus;
  }, [pendingTasks, missedTasks, completedTasks]);

  const todayKey = toLocalYMD(new Date());

  const goMonth = (delta) => {
    const next = new Date(viewDate);
    next.setMonth(viewDate.getMonth() + delta, 1);
    setViewDate(next);
    setSelectedDateKey(null);
  };

  const monthName = viewDate.toLocaleDateString(undefined, { month: "long" });

  const monthStats = useMemo(() => {
    const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const end = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const inRange = (dateStr) => {
      if (!dateStr) return false;
      const dt = new Date(dateStr);
      return dt >= start && dt <= end;
    };

    const stats = { pending: 0, missed: 0, completed: 0 };
    allTasksWithStatus.forEach((t) => {
      if (inRange(t.due_date)) {
        if (t.status === "Pending") stats.pending += 1;
        else if (t.status === "Overdue") stats.missed += 1;
        else stats.completed += 1;
      }
    });
    return stats;
  }, [allTasksWithStatus, viewDate]);

  const upcoming = useMemo(() => {
    const today = new Date();
    const dated = allTasksWithStatus.filter(t => t.due_date).map(t => ({
      ...t,
      due: new Date(t.due_date)
    }));
    dated.sort((a, b) => a.due - b.due);
    return dated.filter(t => t.due >= today).slice(0, 4);
  }, [allTasksWithStatus]);

  const cells = [];
  for (let i = 0; i < monthMeta.startDay; i += 1) cells.push(null);
  for (let day = 1; day <= monthMeta.totalDays; day += 1) {
    cells.push({ day });
  }

  const selectedTasks = useMemo(() => {
    if (!selectedDateKey || !tasksByDate[selectedDateKey]) return [];
    const buckets = tasksByDate[selectedDateKey];
    const withStatus = [
      ...buckets.pending.map(t => ({ ...t, status: "Pending" })),
      ...buckets.missed.map(t => ({ ...t, status: "Overdue" })),
      ...buckets.completed.map(t => ({ ...t, status: "Completed" }))
    ];
    return withStatus.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [selectedDateKey, tasksByDate]);

  return (
    <div className="calendar-shell">
      <div className="calendar-glance">
        <div className="glance-card">
          <div className="glance-label">Pending this month</div>
          <div className="glance-value">{monthStats.pending}</div>
        </div>
        <div className="glance-card">
          <div className="glance-label">Overdue this month</div>
          <div className="glance-value warn">{monthStats.missed}</div>
        </div>
        <div className="glance-card">
          <div className="glance-label">Completed this month</div>
          <div className="glance-value success">{monthStats.completed}</div>
        </div>
        <div className="glance-card">
          <div className="glance-label">Upcoming</div>
          <div className="glance-value">{upcoming.length}</div>
        </div>
      </div>

      <div className="calendar-header">
        <div>
          <div className="calendar-title">{monthName} {monthMeta.year}</div>
          <div className="calendar-sub">Plan tasks by date and jump to details.</div>
        </div>
        <div className="calendar-actions">
          <button onClick={() => goMonth(-1)} aria-label="Previous month">◀</button>
          <button onClick={() => setViewDate(new Date())} aria-label="Today">Today</button>
          <button onClick={() => goMonth(1)} aria-label="Next month">▶</button>
        </div>
      </div>

      <div className="calendar-legend">
        <span className="legend-dot pending" /> Pending
        <span className="legend-dot overdue" /> Overdue
        <span className="legend-dot done" /> Done
        <span className="legend-dot today" /> Today
      </div>

      <div className="calendar-grid">
        {dayLabels.map((label) => (
          <div key={label} className="calendar-day-label">{label}</div>
        ))}
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`pad-${idx}`} className="calendar-day empty" />;
          const key = `${monthMeta.year}-${String(monthMeta.month + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
          const stats = tasksByDate[key] || { pending: [], missed: [], completed: [] };
          const totalForDay = stats.pending.length + stats.missed.length + stats.completed.length;
          const hasTasks = totalForDay > 0;
          const isToday = key === todayKey;
          const isSelected = key === selectedDateKey;
          const handleSelect = () => {
            if (!hasTasks) return;
            setSelectedDateKey(key);
          };
          return (
            <div
              key={key}
              className={`calendar-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""} ${hasTasks ? "" : "no-tasks"}`}
              onClick={hasTasks ? handleSelect : undefined}
              role={hasTasks ? "button" : undefined}
              tabIndex={hasTasks ? 0 : -1}
              onKeyDown={hasTasks ? (e) => { if (e.key === "Enter" || e.key === " ") handleSelect(); } : undefined}
            >
              <div className="calendar-day-number">{cell.day}</div>
              <div className="calendar-badges">
                {stats.pending.length > 0 && <span className="badge pending">{stats.pending.length} pending</span>}
                {stats.missed.length > 0 && <span className="badge overdue">{stats.missed.length} overdue</span>}
                {stats.completed.length > 0 && <span className="badge done">{stats.completed.length} done</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="calendar-bottom">
        {selectedDateKey ? (
          <div className="calendar-details">
            <div className="calendar-details__header">
              <div>
                <div className="calendar-details__title">Tasks on {selectedDateKey}</div>
                <div className="calendar-details__sub">Click a task to open tasks view.</div>
              </div>
              <button onClick={() => setSelectedDateKey(null)} className="calendar-details__close" aria-label="Close details">✕</button>
            </div>
            {selectedTasks.length === 0 ? (
              <div className="calendar-empty">No tasks on this date.</div>
            ) : (
              <div className="calendar-details__list">
                {selectedTasks.map((task) => (
                  <div
                    key={`${selectedDateKey}-${task.id}`}
                    className="calendar-details__item"
                    onClick={() => onNavigateToTasks && onNavigateToTasks(task.status)}
                  >
                    <div className="calendar-details__item-title">{task.title || "Untitled task"}</div>
                    <span className={`chip chip-${task.status.toLowerCase()}`}>{task.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="calendar-upcoming">
            <div className="calendar-details__header">
              <div>
                <div className="calendar-details__title">Upcoming</div>
                <div className="calendar-details__sub">Nearest dated tasks</div>
              </div>
            </div>
            {upcoming.length === 0 ? (
              <div className="calendar-empty">No upcoming tasks with dates.</div>
            ) : (
              <div className="calendar-details__list">
                {upcoming.map((task) => (
                  <div
                    key={`up-${task.id}`}
                    className="calendar-details__item"
                    onClick={() => onNavigateToTasks && onNavigateToTasks(task.status)}
                  >
                    <div>
                      <div className="calendar-details__item-title">{task.title || "Untitled task"}</div>
                      <div className="calendar-details__date">Due {toLocalYMD(task.due_date)}</div>
                    </div>
                    <span className={`chip chip-${task.status.toLowerCase()}`}>{task.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;
